// boot-marker er rein side-effekt (ingen imports) og setter window.__dabihBooted
// slik at failsafen i index.html vet at entry-bundelen faktisk lastet. Den laster
// ingen Capacitor-plugins, og bryter derfor ikke garantien under om at
// plugin-registreringen må skje før plugins tas i bruk.
import './boot-marker.js';

// MÅ være først — pre-registrerer Capacitor-plugins med statiske web-factories
// for å unngå "Cannot read properties of undefined (reading 'XxxWeb')"-feilen
// fra pakkenes egne dynamiske `() => import('./web')`-stier. Se issue #12862, #12824.
import './capacitor-plugin-registration.js';
// i18n MÅ initialiseres før app-root (og dermed alle komponenter) slik at første
// paint er på riktig språk. Se src/i18n/index.ts. (issue #13280)
import './i18n/index.js';
import './app-root.js';

import { registerSW } from 'virtual:pwa-register';
import { App } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { runningUnderNativePlatform } from './store/running-under-native-platform';
import { LIVEUPDATE_CHANNEL } from './config/liveupdate-channel';
import { getStateClient } from './client/state-client.js';
import { leaveBreadcrumb, recordEvent, resolveRoute } from './utilities/monitoring.js';

// Sjekk periodisk + på relevante hendelser om det finnes en ny service worker.
// Faner som står åpne lenge navigerer aldri, så uten dette utløses verken
// nettleserens egen SW-sjekk eller en oppdatering – og brukere blir hengende
// på gammel versjon. Når update() finner ny SW kaller SW-en skipWaiting() +
// clients.claim(), og vite-plugin-pwa (autoUpdate) laster siden stille på nytt.
const SW_UPDATE_INTERVAL_MS = 30 * 60 * 1000; // sjekk hver 30. minutt
registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) {
      return;
    }

    const checkForUpdate = () => {
      if (document.visibilityState !== 'visible') {
        return; // ikke sjekk når fanen er i bakgrunnen
      }
      if (!navigator.onLine) {
        return;
      }
      registration.update().catch((err) => console.warn('[SW] update check failed', err));
    };

    setInterval(checkForUpdate, SW_UPDATE_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    });
    window.addEventListener('online', checkForUpdate);
    window.addEventListener('focus', checkForUpdate);
  },
});

// Initialize the StateClient singleton (connects to custom Service Worker)
if ('serviceWorker' in navigator) {
  const stateClient = getStateClient();
  stateClient.onMessage((message) => {
    if (import.meta.env.DEV) {
      console.debug('[StateClient] Received from SW:', message.type, message);
    }
    // Forward SW fetch telemetry to CloudWatch RUM (#13421). The SW broadcasts
    // to every open tab; only the visible one records, so multi-tab sessions
    // don't duplicate events.
    if (message.type === 'SW_TELEMETRY' && document.visibilityState === 'visible') {
      const payload = message.payload;
      if (payload.kind === 'fetch-timing') {
        recordEvent('dabih_sw_fetch', {
          organizationId: payload.organizationId,
          ms: payload.ms,
          bytes: payload.bytes,
        });
      } else {
        recordEvent('dabih_sw_error', {
          organizationId: payload.organizationId,
          ms: payload.ms,
          code: payload.code,
        });
      }
    }
  });
}

// Chunk-lasting kan feile når en fane har stått åpen forbi en deploy (stale
// chunk-hasher) eller mistet nettet (#13442). Reload henter fersk index.html
// med ferske hasher — men maks én gang per tidsvindu, ellers reload-looper en
// klient uten nett. Uten reload får featuren feile lokalt: uten preventDefault
// rejecter import()-promiset som normalt (preventDefault ville fått det til å
// resolve `undefined` — bug-klassen beskrevet i capacitor-plugin-registration.ts).
// aws-rum-web flusher batchen på pagehide, så eventet overlever reloaden.
const CHUNK_RELOAD_KEY = 'dabih-chunk-reload-at';
const CHUNK_RELOAD_MIN_INTERVAL_MS = 10 * 60 * 1000;
window.addEventListener('vite:preloadError', (event) => {
  let lastReloadAt: number;
  try {
    lastReloadAt = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0', 10) || 0;
  } catch {
    // sessionStorage utilgjengelig – behandl som nylig reloadet (ingen loop-vakt ellers).
    lastReloadAt = Date.now();
  }
  const canReload = Date.now() - lastReloadAt > CHUNK_RELOAD_MIN_INTERVAL_MS;
  recordEvent('dabih_preload_error', {
    message: (event as unknown as { payload?: Error }).payload?.message ?? 'unknown',
    route: resolveRoute(window.location.pathname),
    reloading: canReload,
  });
  if (canReload) {
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      // ignorer – Date.now()-fallbacken over tar over neste gang.
    }
    event.preventDefault();
    window.location.reload();
  }
});

// SafeArea plugin (@capacitor-community/safe-area@7.0.0-beta.3) works automatically.
// It sets CSS env(safe-area-inset-*) variables without requiring JavaScript initialization.
// The plugin is enabled automatically on iOS, Android, and web.
// Just use the CSS variables in your styles, e.g.:
//   padding-top: env(safe-area-inset-top, 0px);
//
// On native platforms, edge-to-edge mode must be enabled in MainActivity/AppDelegate,
// which we've done in android/app/src/main/java/no/trinnvis/dabih/issues/MainActivity.java

// Setup App Update and LiveUpdate listeners on all native platforms (iOS/Android, phones and tablets)
// Can be disabled for local development by setting localStorage flag
if (runningUnderNativePlatform()) {
  App.addListener('resume', async () => {
    // Skip update checks if offline - avoid unnecessary network errors
    if (!navigator.onLine) {
      console.log('[Resume] Device is offline, skipping update checks');
      return;
    }

    // Check for app updates first (native app version)
    try {
      const updateInfo = await AppUpdate.getAppUpdateInfo();

      if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        // Store update info for UI to display (could show a banner or dialog);
        // the UI decides when to prompt the user.
        localStorage.setItem('appUpdateAvailable', 'true');
        localStorage.setItem('appUpdateVersion', updateInfo.availableVersionName || '');
        leaveBreadcrumb('App update available', {
          currentVersion: updateInfo.currentVersionName,
          availableVersion: updateInfo.availableVersionName,
        });
      } else {
        localStorage.removeItem('appUpdateAvailable');
        localStorage.removeItem('appUpdateVersion');
      }
    } catch (error) {
      console.error('[AppUpdate] Failed to check for updates:', error);
    }

    // Then check for LiveUpdate (JavaScript/HTML/CSS updates)
    // Check if LiveUpdate is disabled for development
    const liveUpdateDisabled = localStorage.getItem('disableLiveUpdate') === 'true';
    if (liveUpdateDisabled) {
      console.log('[LiveUpdate] Skipping - disabled via localStorage flag (disableLiveUpdate=true)');
      return;
    }

    try {
      // Store last sync timestamp
      localStorage.setItem('liveUpdateLastSync', new Date().toISOString());

      const { nextBundleId } = await LiveUpdate.sync({ channel: LIVEUPDATE_CHANNEL });
      if (nextBundleId) {
        // Milestone worth having in RUM: errors right after an OTA update can
        // then be correlated with the bundle switch. Flushed on pagehide, so it
        // survives the reload.
        leaveBreadcrumb('LiveUpdate: applying new bundle', { bundleId: nextBundleId, channel: LIVEUPDATE_CHANNEL });
        console.log(`[LiveUpdate] New bundle available: ${nextBundleId}, reloading...`);
        await LiveUpdate.reload();
      }
    } catch (error) {
      // Don't reload if LiveUpdate sync fails (e.g., network issues)
      leaveBreadcrumb('LiveUpdate: sync failed', { message: error instanceof Error ? error.message : String(error) });
      console.warn('[LiveUpdate] Sync failed, continuing without update:', error);
    }
  });

  // Log initial status
  const liveUpdateDisabled = localStorage.getItem('disableLiveUpdate') === 'true';
  console.log(
    `[LiveUpdate] Status: ${liveUpdateDisabled ? '❌ DISABLED (local dev)' : '✅ ENABLED (will sync on resume)'}`,
  );
  if (!liveUpdateDisabled) {
    console.log(
      '[LiveUpdate] To disable for local development, run: localStorage.setItem("disableLiveUpdate", "true")',
    );
  }
}
